/**
 * お気に入り画面
 * お気に入り登録された銘柄のスナップショット一覧を表示
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllFavorites, FavoriteSnapshot } from '../../services/favoritesService';
import { FavoriteButton } from '../FavoriteButton';
import '../../styles/Favorites.css';

const Favorites: React.FC = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState<FavoriteSnapshot[]>([]);
  const [sortBy, setSortBy] = useState<'added' | 'confidence' | 'date'>('added');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // お気に入り一覧を読み込み
  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = () => {
    try {
      const allFavorites = getAllFavorites();
      setFavorites(allFavorites);
    } catch (error) {
      console.error('❌ お気に入り読み込みエラー:', error);
      alert(`お気に入りの読み込みに失敗しました: ${error}`);
    }
  };

  // ソート処理
  const sortedFavorites = [...favorites].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'added':
        comparison = new Date(a.added_at).getTime() - new Date(b.added_at).getTime();
        break;
      case 'confidence':
        comparison = a.detection_result.confidence_score - b.detection_result.confidence_score;
        break;
      case 'date':
        comparison = new Date(a.detection_result.change_point_date).getTime() - new Date(b.detection_result.change_point_date).getTime();
        break;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // 銘柄クリックで詳細画面へ
  const handleStockClick = (snapshot: FavoriteSnapshot) => {
    // スナップショットの完全性をチェック
    if (!snapshot.detection_result.detection_reason) {
      alert(`エラー: ${snapshot.symbol} のスナップショットに分析詳細が含まれていません。\n\nこのお気に入りは古い形式で保存されています。\n一度削除して、詳細画面から再登録してください。`);
      return;
    }

    if (!snapshot.detection_result.metrics) {
      alert(`エラー: ${snapshot.symbol} のスナップショットに詳細メトリクスが含まれていません。\n\nこのお気に入りは古い形式で保存されています。\n一度削除して、詳細画面から再登録してください。`);
      return;
    }

    if (!snapshot.detection_result.required_days) {
      alert(`エラー: ${snapshot.symbol} のスナップショットに必要日数情報が含まれていません。\n\nこのお気に入りは古い形式で保存されています。\n一度削除して、詳細画面から再登録してください。`);
      return;
    }

    // StockDetailが期待する形式でLocalStorageに保存
    const stockData = {
      symbol: snapshot.symbol,
      company: snapshot.company_name,
      confidence_score: snapshot.detection_result.confidence_score,
      change_date: snapshot.detection_result.change_point_date,
      stage: snapshot.detection_result.confidence_score >= 70 ? '確定' : '兆候',
      detection_reason: snapshot.detection_result.detection_reason,
      algorithm: snapshot.detection_result.algorithm_id,
      metrics: snapshot.detection_result.metrics,
      isNew: false,
      parameters: snapshot.detection_result.parameters,
      required_days: snapshot.detection_result.required_days,  // 必要日数を含める
      detection_date: snapshot.detection_date  // 検出実行日を含める（お気に入り登録時の日付）
    };

    localStorage.setItem('selectedStockData', JSON.stringify(stockData));

    navigate(`/stocks/${snapshot.symbol}`);
  };

  // お気に入り解除時にリストを更新
  const handleFavoriteToggle = (isFavorited: boolean) => {
    if (!isFavorited) {
      // お気に入り解除されたらリストを再読み込み
      loadFavorites();
    }
  };

  return (
    <div className="favorites-page">
      <div className="favorites-header">
        <h1>お気に入り銘柄</h1>
        <div className="favorites-count">
          {favorites.length}件の銘柄
        </div>
      </div>

      {/* ソートコントロール */}
      <div className="favorites-controls">
        <div className="sort-section">
          <span className="sort-label">並び替え:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="sort-select"
          >
            <option value="added">登録日時</option>
            <option value="confidence">信頼度</option>
            <option value="date">検出日時</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="sort-order-button"
          >
            {sortOrder === 'asc' ? '↑ 昇順' : '↓ 降順'}
          </button>
        </div>
      </div>

      {/* お気に入り一覧 */}
      <div className="favorites-list">
        {sortedFavorites.length === 0 ? (
          <div className="empty-message">
            <p>お気に入りに登録されている銘柄がありません</p>
            <p className="empty-hint">
              ダッシュボードや詳細画面で ☆ をクリックしてお気に入りに追加できます
            </p>
          </div>
        ) : (
          sortedFavorites.map((snapshot) => (
            <div key={snapshot.symbol} className="favorite-item">
              <div className="favorite-item-header">
                <div className="favorite-item-actions">
                  <FavoriteButton
                    snapshot={snapshot}
                    size="medium"
                    onToggle={handleFavoriteToggle}
                  />
                </div>
                <div
                  className="favorite-item-content"
                  onClick={() => handleStockClick(snapshot)}
                >
                  <div className="favorite-basic">
                    <span className="favorite-symbol">{snapshot.symbol}</span>
                    <span className="favorite-company">{snapshot.company_name}</span>
                  </div>
                  <div className="favorite-metrics">
                    <div className="favorite-metric">
                      <span className="metric-label">信頼度:</span>
                      <span className="metric-value">
                        {Math.floor(snapshot.detection_result.confidence_score)}%
                      </span>
                    </div>
                    <div className="favorite-metric">
                      <span className="metric-label">アルゴリズム:</span>
                      <span className="metric-value">
                        {snapshot.detection_result.algorithm_name}
                      </span>
                    </div>
                    <div className="favorite-metric">
                      <span className="metric-label">変化点:</span>
                      <span className="metric-value">
                        {snapshot.detection_result.change_point_date}
                      </span>
                    </div>
                    <div className="favorite-metric">
                      <span className="metric-label">検出日時:</span>
                      <span className="metric-value">
                        {new Date(snapshot.detection_date).toLocaleString('ja-JP', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    {(() => {
                      // company_info または LocalStorage の個別キャッシュから AI分析情報を確認
                      const hasCompanyInfo = snapshot.company_info || (() => {
                        try {
                          // 個別キャッシュキー: companyInfo_{symbol}
                          const cache = localStorage.getItem(`companyInfo_${snapshot.symbol}`);
                          if (cache) {
                            return JSON.parse(cache);
                          }
                        } catch (e) {
                          return null;
                        }
                        return null;
                      })();

                      return hasCompanyInfo ? (
                        <div className="favorite-metric has-ai-info">
                          <span className="metric-label">AI分析:</span>
                          <span className="metric-value ai-badge">✓ 取得済み</span>
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Favorites;
